import { Eye } from "@layer/icons";
import type { Table } from "@tanstack/react-table";
import type { ComponentType } from "react";
import {
	Button,
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuPortal,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
} from "../../../components";

type DataTableSettingsProps<TData> = {
	table: Table<TData>;
};

type DataTableDropdownOptions = {
	label: string;
	value: string;
	icon?: ComponentType<{ className?: string }>;
};

const DataTableSettings = <TData,>({
	table,
}: DataTableSettingsProps<TData>) => {
	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild className="mr-1 mt-[2px]">
				<Button variant="ghost" className="px-2">
					<Eye className="h-4 w-4" />
				</Button>
			</DropdownMenuTrigger>

			<DropdownMenuContent className="min-w-[200px]">
				<DropdownMenuGroup>
					{table
						.getAllColumns()
						.filter(
							(column) =>
								typeof column.accessorFn !== "undefined" &&
								column.getCanHide(),
						)
						.map((column) => {
							return (
								<DropdownMenuCheckboxItem
									key={column.id}
									className="capitalize"
									checked={column.getIsVisible()}
									onSelect={(e) => e.preventDefault()}
									onCheckedChange={(value) =>
										column.toggleVisibility(!!value)
									}
								>
									{column.id}
								</DropdownMenuCheckboxItem>
							);
						})}
				</DropdownMenuGroup>
				{table
					.getAllColumns()
					.filter((column) => column.columnDef.meta)
					.map(({ columnDef: { meta } }) => (
						<DropdownMenuSub key={meta?.id}>
							<DropdownMenuSubTrigger>
								{meta?.label}
							</DropdownMenuSubTrigger>
							<DropdownMenuPortal>
								<DropdownMenuSubContent className="min-w-[180px]">
									{meta?.values?.map(
										({
											value,
											label,
										}: DataTableDropdownOptions) => {
											const column = table.getColumn(
												meta.id,
											);
											const facets =
												column?.getFacetedUniqueValues();
											const selectedValues = new Set(
												column?.getFilterValue() as string[],
											);
											const isSelected =
												selectedValues.has(value);
											return (
												<DropdownMenuCheckboxItem
													className="flex min-w-max items-center"
													key={value}
													checked={isSelected}
													onSelect={(e) =>
														e.preventDefault()
													}
													onCheckedChange={() => {
														if (isSelected) {
															selectedValues.delete(
																value,
															);
														} else {
															selectedValues.add(
																value,
															);
														}
														const filterValues =
															Array.from(
																selectedValues,
															);
														column?.setFilterValue(
															filterValues.length
																? filterValues
																: undefined,
														);
													}}
												>
													<span>{label}</span>
													{facets?.get(value) && (
														<span className="ml-auto flex h-4 w-4 items-center justify-center font-mono text-xs">
															{facets.get(value)}
														</span>
													)}
												</DropdownMenuCheckboxItem>
											);
										},
									)}
								</DropdownMenuSubContent>
							</DropdownMenuPortal>
						</DropdownMenuSub>
					))}
			</DropdownMenuContent>
		</DropdownMenu>
	);
};

export { DataTableSettings };
